import React, { useCallback, useMemo, useState } from "react";

import { getApiUrl } from "@/config";
import InteractionService from "@/service/Interaction";
import LocalStorageService from "@/service/LocalStorage";
import {
  Voting,
  VotingResult,
  VotingResultsContainer,
  VotingSlide,
  VotingVote,
} from "@/service/Model";
import TrackingService from "@/service/Tracking";

export type VotingProviderProps = {
  interaction: Voting;
  interactionResults: VotingResultsContainer;
  preview: boolean;
  children?: React.ReactNode;
};

export type VotingContextProps = {
  votes: VotingVote[];
  preview: boolean;
  participated: boolean;
  registered: boolean;
  addVotes: (slide: VotingSlide, votes: VotingVote[]) => void;
  register: () => void;
  submit: () => void;
  slideVoted: (slideId: string) => boolean;
  finished: () => boolean;
  resolvePercent: (slide: VotingSlide, optionId: string) => number | undefined;
};

const VotingContext = React.createContext<VotingContextProps>({
  votes: [],
  preview: false,
  participated: false,
  registered: false,
  addVotes: () => undefined,
  register: () => undefined,
  submit: () => undefined,
  slideVoted: () => false,
  finished: () => false,
  resolvePercent: () => undefined,
});

const VotingProvider: React.FC<VotingProviderProps> = ({
  interaction,
  interactionResults,
  preview,
  children,
}) => {
  const [votes, setVotes] = useState<VotingVote[]>(
    !preview
      ? (LocalStorageService.getVotes(interaction.id) as VotingVote[]) || []
      : []
  );
  const [participated, setParticipated] = useState<boolean>(
    !preview ? LocalStorageService.getParticipation(interaction.id) : false
  );
  const [registered, setRegistered] = useState<boolean>(false);

  const api = useMemo(
    () => new InteractionService(getApiUrl(preview)),
    [preview]
  );

  const slideVoted = useCallback(
    (slideId: string) => votes.some((vote) => vote.slideId === slideId),
    [votes]
  );

  const finished = useCallback(
    () =>
      interaction.slides.every((slide) => {
        const slideVotes = votes.filter((vote) => vote.slideId === slide.id);
        return slide.voteCount ? slideVotes.length === slide.voteCount : false;
      }),
    [interaction.slides, votes]
  );

  const addVotes = useCallback(
    (slide: VotingSlide, slideVotes: VotingVote[]) => {
      const updatedVotes = [
        ...votes.filter((vote) => vote.slideId !== slide.id),
        ...slideVotes,
      ];
      if (!preview) {
        LocalStorageService.setVotes(interaction.id, updatedVotes);
      }
      setVotes(updatedVotes);
    },
    [votes, interaction.id, preview]
  );

  const submit = useCallback(() => {
    if (!preview) {
      TrackingService.getInstance().trackOnVote();
      const results = votes.map((vote) => ({ ...vote, value: 1 }));
      api
        .postVotingResults(interaction.id, results, !participated)
        .then(() => LocalStorageService.setParticipation(interaction.id, true));
    }
    setParticipated(true);
  }, [votes, interaction.id, participated, preview, api]);

  const resolvePercent = useCallback(
    (slide: VotingSlide, optionId: string): number | undefined => {
      const localStorageIsUserAnswer = votes.some(
        (vote: VotingVote) => vote.optionId === optionId
      );

      const slideResults = interactionResults.results.filter(
        (result: VotingResult) => result.slideId === slide.id
      );

      const slideVotes = votes.filter(
        (vote: VotingVote) => vote.slideId === slide.id
      );
      const localStorageHasParticipated = slideVotes.length === slide.voteCount;

      return getVotingOptionVotesPercent(
        slideResults,
        optionId,
        slide.voteCount,
        localStorageHasParticipated,
        localStorageIsUserAnswer
      );
    },
    [interactionResults, votes]
  );

  const register = useCallback(() => setRegistered(true), []);

  const value = useMemo(
    () => ({
      votes,
      preview,
      participated,
      registered,
      addVotes,
      register,
      submit,
      slideVoted,
      finished,
      resolvePercent,
    }),
    [
      votes,
      preview,
      participated,
      registered,
      addVotes,
      register,
      submit,
      slideVoted,
      finished,
      resolvePercent,
    ]
  );

  return (
    <VotingContext.Provider value={value}>{children}</VotingContext.Provider>
  );
};

const VotingConsumer = VotingContext.Consumer;

export { VotingConsumer, VotingProvider };
export default VotingContext;

const getVotingOptionVotesPercent = (
  slideResults: VotingResult[],
  optionId: string,
  slideVoteCount: number,
  localStorageHasParticipated: boolean,
  localStorageIsUserAnswer: boolean
): number => {
  const optionResults: VotingResult[] = slideResults.filter(
    (result: VotingResult) => result.optionId === optionId
  );
  const optionResult: VotingResult | undefined =
    optionResults.length > 0 ? optionResults[0] : undefined;
  let optionVotes: number = optionResult ? optionResult.value : 0;
  let totalVotes = slideResults
    .map((r: VotingResult) => r.value)
    .reduce((a: number, b: number): number => a + b, 0);

  // Maybe a counting problem:
  // Assume: If you are the first participant, the results are empty.
  // If you click on an option, you must see "100% of the participants pick your answer"
  // Problem:
  // - you come back or reload the page and
  // - the caching time is expired and
  // - your answer is now in the official results response
  // => then your answer will be counted twice and the percentage is wrong
  if (localStorageIsUserAnswer) {
    // Your vote is not in the result, because of cached results (cloudfront)
    // Plus user vote from local storage
    optionVotes += 1;
  }

  if (localStorageHasParticipated) {
    // Your vote is not in the result, because of cached results (cloudfront)
    // Plus voteCount from local storage
    totalVotes += slideVoteCount;
  }

  // Because slideVoteCount is the same count for EVERY participant, we can divide by that.
  // So we can interpolate: 1 vote = 1 participant
  totalVotes = totalVotes / slideVoteCount;

  return Math.round((optionVotes / totalVotes) * 100);
};
